import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FaLinkedin, FaLinkedinIn, FaStar, FaYoutube, FaInstagram, FaFacebookF,
  FaGithub, FaMedium, FaWordpressSimple, FaBloggerB, FaPatreon, FaQuora,
  FaRss, FaPodcast, FaHandshake,
} from "react-icons/fa";
import { FaTiktok, FaXTwitter } from "react-icons/fa6";
import { SiSubstack } from "react-icons/si";
import { Logo } from "./Logo";
import { SOCIALS } from "../lib/socials";
import { subscribeNewsletter } from "../lib/api";

const ICONS = {
  linkedin_company: FaLinkedin,
  linkedin_personal: FaLinkedinIn,
  influential_women: FaStar,
  tiktok: FaTiktok,
  youtube: FaYoutube,
  instagram: FaInstagram,
  facebook: FaFacebookF,
  x: FaXTwitter,
  github: FaGithub,
  myspace: FaStar,
  medium: FaMedium,
  wordpress: FaWordpressSimple,
  blogger: FaBloggerB,
  substack: SiSubstack,
  podcast: FaPodcast,
  patreon: FaPatreon,
  quora: FaQuora,
  rss: FaRss,
  alignable: FaHandshake,
};

export const Footer = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Welcome — check your inbox for a note from Felicia.");
      setEmail("");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="bg-emerald text-cream" data-testid="footer">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-24 pb-12">
        <div className="grid lg:grid-cols-12 gap-12 border-b border-white/15 pb-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <Logo className="w-10 h-10 text-gold" stroke="#D4AF37" />
              <span className="font-display text-2xl">The Love Genome™</span>
            </div>
            <p className="font-editorial text-2xl leading-snug text-cream/85 max-w-md">
              Love is the source. Virtues are the genes. Character is the expression. Relationships are the ecosystem. Human flourishing is the outcome.
            </p>
          </div>

          <div className="lg:col-span-3 lg:col-start-7">
            <p className="eyebrow text-gold mb-6">Explore</p>
            <ul className="space-y-3 font-body text-cream/80">
              <li><Link to="/store" className="link-underline" data-testid="footer-store">Store & Merch</Link></li>
              <li><Link to="/academy" className="link-underline" data-testid="footer-academy">Academy</Link></li>
              <li><Link to="/circles" className="link-underline" data-testid="footer-circles">Reader Circles</Link></li>
              <li><Link to="/orders" className="link-underline" data-testid="footer-orders">Order Lookup</Link></li>
              <li><a href="/#framework" className="link-underline">The Framework</a></li>
              <li><a href="/#work" className="link-underline">Work With Felicia</a></li>
              <li><a href="/#author" className="link-underline">About Felicia</a></li>
              <li><a href="/#faq" className="link-underline">FAQ</a></li>
              <li><a href="/#contact" className="link-underline">Contact</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="eyebrow text-gold mb-6">Join the movement</p>
            <p className="font-body text-cream/70 text-sm mb-4">
              Early access to the companion app, events, and new editions.
            </p>
            <form onSubmit={submit} className="flex flex-col gap-3" data-testid="footer-newsletter-form">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email"
                data-testid="footer-newsletter-input"
                className="bg-transparent border-b border-cream/30 focus:border-gold outline-none py-2 text-cream placeholder:text-cream/40 transition-colors"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="footer-newsletter-submit"
                className="btn-gold justify-center mt-2 disabled:opacity-60"
              >
                {loading ? "Joining…" : "Subscribe"}
              </button>
            </form>
          </div>
        </div>

        <div className="py-12 border-b border-white/15">
          <p className="eyebrow text-gold mb-8 text-center md:text-left">Connect with The Love Genome™</p>
          <div className="flex flex-wrap gap-3 justify-center md:justify-start" data-testid="social-links">
            {SOCIALS.map((s) => {
              const Icon = ICONS[s.key] || FaStar;
              return s.url ? (
                <a
                  key={s.key}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  title={s.label}
                  data-testid={`social-${s.key}`}
                  className="w-11 h-11 flex items-center justify-center border border-white/20 text-cream/80 hover:bg-gold hover:text-ink hover:border-gold transition-all duration-300"
                >
                  <Icon className="text-base" />
                </a>
              ) : (
                <span
                  key={s.key}
                  aria-label={`${s.label} — coming soon`}
                  title={`${s.label} — coming soon`}
                  data-testid={`social-${s.key}`}
                  className="w-11 h-11 flex items-center justify-center border border-white/10 text-cream/30 cursor-default"
                >
                  <Icon className="text-base" />
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col md:flex-row justify-between items-center gap-4 pt-10 text-cream/50 text-xs font-body">
          <p>© {new Date().getFullYear()} The Love Genome™ by Felicia T. Johnson. All rights reserved.</p>
          <p>A universal framework for how love becomes flourishing.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
